import { ColumnDef } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { DragDropChangeValue } from "components";
import { moveElementInArray } from "helpers";
import { useLanguage } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { t } from "i18next";
import { useMemo, useState } from "react";
import {
  ItemsTableDragDrop,
  RowDragHandleCell,
  TableItemActionsControl
} from "ui";

import { useFeedInsertsReorder } from "../../apis";
import { FeedInsertsListItem } from "../../content.types";
import { NameAndPreviewCell } from "./atoms";
import { FeedInsertsTableProps } from "./FeedInsertsTable.types";

import styles from "./FeedInsertsTable.module.scss";

const FeedInsertsTable = ({
  feedInserts,
  sorting,
  setSorting,
  onDelete,
  onEdit,
  setFeedInserts,
  isDragDisabled,
  isLoading
}: FeedInsertsTableProps) => {
  const { currentLanguage } = useLanguage();

  const feedInsertsReorder = useFeedInsertsReorder();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const onChangeBannerPriority = (value: DragDropChangeValue) => {
    const { draggableId, toKeyForReorder, fromIndex, toIndex } = value;

    setFeedInserts(
      moveElementInArray({
        array: feedInserts,
        keyForReorder: "order",
        fromIndex,
        toIndex
      })
    );

    feedInsertsReorder.mutate({
      id: draggableId,
      newOrder: toKeyForReorder !== undefined ? toKeyForReorder : toIndex
    });
  };

  const columns: Array<ColumnDef<FeedInsertsListItem, FeedInsertsListItem[]>> =
    useMemo(
      () => [
        {
          id: "dragHandle",
          header: "",
          enableSorting: false,
          size: 3,
          minSize: 3,
          cell: (cell) => (
            <RowDragHandleCell
              rowId={cell.row.id}
              isDragDisabled={isDragDisabled}
            />
          )
        },
        {
          header: t("Name"),
          accessorKey: "Name",
          size: 35,
          minSize: 35,
          cell: ({ row }) => <NameAndPreviewCell feedInsert={row.original} />
        },
        {
          id: "form",
          enableSorting: false,
          header: t("Form"),
          size: 15,
          minSize: 15,
          cell: ({ row }) => row.original.form.replace(/([A-Z])/g, " $1").trim()
        },
        {
          id: "Type",
          header: t("Type"),
          enableSorting: false,
          size: 15,
          minSize: 15,
          cell: ({ row }) => row.original.type.replace(/([A-Z])/g, " $1").trim()
        },

        {
          id: "titleAndDescription",
          header: t("Title & Description"),
          size: 31,
          minSize: 31,
          cell: ({ row }) => (
            <>
              <div>{row.original.header}</div>
              <div className={styles.feedInsertsTable_description}>
                {row.original.text}
              </div>
            </>
          )
        },

        {
          accessorKey: "dotsColumn",
          header: () => null,
          size: 4,
          minSize: 4,
          cell: ({ row }) => (
            <TableItemActionsControl
              actionsList={[
                {
                  label: "Edit",
                  Icon: <EditIcon fill={"#090914"} width={18} height={18} />,
                  onClick: () => onEdit(row.original)
                },
                {
                  label: "Delete",
                  Icon: (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: "#ff0033",
                  onClick: () => onDelete(row.original.id)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          ),
          enableSorting: false
        }
      ],
      [feedInserts, openedItemOptionsIndex, currentLanguage]
    );

  return (
    <ItemsTableDragDrop
      className={styles.feedInsertsTable}
      columns={columns}
      data={feedInserts}
      sorting={sorting}
      setSorting={setSorting}
      isLoading={isLoading}
      skeletonHeight={65}
      droppableId={"feedInsertsList"}
      keyForReorder={"order"}
      onChange={onChangeBannerPriority}
      isDragDisabled={isDragDisabled}
    />
  );
};

export default FeedInsertsTable;
